import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { cx } from "@/components/FormFields";

const input =
  "w-full bg-[#0d120e] border border-[#232f25] rounded-md px-3.5 py-2.5 text-sm text-[#f4f7f4] placeholder:text-[#69776b] outline-none transition-colors focus:border-[#8CE63C] focus:ring-2 focus:ring-[#8CE63C]/20";

export const useCaptcha = () => {
  const [captcha, setCaptcha] = useState({ question: "", token: "" });
  const load = () => api.get("/captcha").then((r) => setCaptcha(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  return { captcha, reload: load };
};

// Generic inquiry form used by Special Requests + Contact
export const InquiryForm = ({ type, fields, submitLabel, thankYou, testIdPrefix }) => {
  const [form, setForm] = useState({});
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const { captcha, reload } = useCaptcha();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const missing = fields.filter((f) => f.required && !String(form[f.key] || "").trim());
    if (missing.length) { toast.error("Please complete all required fields."); return; }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { toast.error("Enter a valid email address."); return; }
    if (!captchaAnswer.trim()) { toast.error("Please answer the verification question."); return; }
    setSubmitting(true);
    try {
      await api.post("/inquiries", {
        type,
        name: form.name || "",
        email: form.email || "",
        phone: form.phone || "",
        unit_type: form.unit_type || "",
        intended_use: form.intended_use || "",
        quantity: form.quantity || "",
        budget: form.budget || "",
        subject: form.subject || "",
        message: form.message || "",
        captcha_token: captcha.token,
        captcha_answer: captchaAnswer,
        company_url: honeypot,
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed.");
      reload();
      setCaptchaAnswer("");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="erp-fade-up rounded-2xl border border-[#232f25] bg-[#111712] p-8 sm:p-12 text-center" data-testid={`${testIdPrefix}-thankyou`}>
        <div className="mx-auto h-16 w-16 rounded-full bg-[#132717] border border-[#8CE63C] flex items-center justify-center erp-lime-glow">
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#8CE63C" strokeWidth="3"><path d="M20 6L9 17l-5-5" /></svg>
        </div>
        <h2 className="font-display text-3xl font-black text-white uppercase tracking-tight mt-6">{thankYou.title}</h2>
        <p className="text-[#9EABA0] text-sm sm:text-base leading-relaxed mt-4 max-w-md mx-auto">{thankYou.body}</p>
        <button
          onClick={() => { setDone(false); setForm({}); setCaptchaAnswer(""); reload(); }}
          className="inline-block mt-8 px-6 py-3 rounded-md bg-[#0d120e] border border-[#232f25] text-sm font-semibold text-[#f4f7f4] hover:border-[#8CE63C]/50 transition-colors"
        >
          Send Another
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-2xl border border-[#232f25] bg-[#111712] p-6 sm:p-8 space-y-5" data-testid={`${testIdPrefix}-form`}>
      <input type="text" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
      {fields.map((f) => (
        <div key={f.key} className={f.full ? "" : ""}>
          <label className="block text-xs font-semibold uppercase tracking-wide text-[#9EABA0] mb-1.5">
            {f.label}{f.required && <span className="text-[#8CE63C] ml-1">*</span>}
          </label>
          {f.type === "textarea" ? (
            <textarea rows={f.rows || 5} className={cx(input, "resize-y")} placeholder={f.placeholder} value={form[f.key] || ""} onChange={set(f.key)} data-testid={`${testIdPrefix}-${f.key}`} />
          ) : f.type === "select" ? (
            <select className={cx(input, "cursor-pointer appearance-none")} value={form[f.key] || ""} onChange={set(f.key)} data-testid={`${testIdPrefix}-${f.key}`}>
              <option value="">Select…</option>
              {f.options.map((o) => <option key={o} value={o} className="bg-[#111712]">{o}</option>)}
            </select>
          ) : (
            <input type={f.type || "text"} className={input} placeholder={f.placeholder} value={form[f.key] || ""} onChange={set(f.key)} data-testid={`${testIdPrefix}-${f.key}`} />
          )}
        </div>
      ))}

      <div className="max-w-xs">
        <label className="block text-xs font-semibold uppercase tracking-wide text-[#9EABA0] mb-1.5">
          Verification — {captcha.question || "…"}<span className="text-[#8CE63C] ml-1">*</span>
        </label>
        <input value={captchaAnswer} onChange={(e) => setCaptchaAnswer(e.target.value)} inputMode="numeric" placeholder="Your answer" className={input} data-testid={`${testIdPrefix}-captcha`} />
      </div>

      <button type="submit" disabled={submitting} className="w-full sm:w-auto px-8 py-3.5 rounded-md bg-[#8CE63C] text-[#080b08] text-sm font-black uppercase tracking-wider hover:bg-[#9df04f] transition-colors disabled:opacity-50" data-testid={`${testIdPrefix}-submit`}>
        {submitting ? "Submitting…" : submitLabel}
      </button>
    </form>
  );
};
